// Scroll-driven motion: how articles arrive, move and hand over to each other.
//
//   desktop   entrances (text rises, pictures open behind a wipe, headings from behind
//             their baseline)
//             and the parallax that drifts panels and images against the strip
//   mobile    the same entrances with the fx-* classes of _effects.scss, layered parallax,
//             and the hand-off as an article leaves the top of the screen
//
// Every effect here is temporary: once a block has arrived, its inline styles are
// cleared, so the magazine at rest looks exactly as it does without any of this. All of it
// is skipped for prefers-reduced-motion (boot.js builds the layouts through gsap.matchMedia).
//
// Everything runs on GSAP's ticker, which sleeps when nothing moves: no per-element
// requestAnimationFrame loops.

// ------------------------------------------------------------------------- shared ----

// The blocks an article assembles from: its top-level content, or where the wrap holds a
// single block, the parts inside it. Enough pieces to feel assembled, never so many that
// a paragraph arrives a word at a time.
mag.fxBlocks = (section) => {
    let group = [...(section.querySelector('.wrap') || section).children];
    let depth = 0;
    while (group.length === 1 && group[0].children.length > 1 && depth < 2) {
        group = [...group[0].children];
        depth++;
    }

    // `.heading-animation` has its own entrance (initHeadings). Structural filler (`<br>`
    // is `display: block` in our reset) and empty elements would burn a slot in the stagger.
    // Nor anything that isn't rendered: the calculators' popup content sits in the article
    // as display: none until Fancybox shows it, and would be shown still waiting to arrive.
    const noise = /^(BR|SCRIPT|STYLE|TEMPLATE|INPUT)$/;
    return group.filter(el =>
        el.getClientRects().length > 0 &&
        !el.classList.contains('heading-animation') &&
        !el.classList.contains('parallax-image') &&
        !noise.test(el.tagName) &&
        !(el.textContent.trim().length === 0 && !mag.fxHasMedia(el, 'img, video, svg, canvas, iframe')));
};

mag.fxHasMedia = (el, sel) => el.matches(sel) || !!el.querySelector(sel);

// A picture block — no words of its own — opens behind a wipe; anything else rises.
// Wiping a block of text would read as a glitch.
mag.fxIsMedia = (el) =>
    el.matches('.picture, .video-player') ||
    (mag.fxHasMedia(el, 'img, video') && el.textContent.trim().length === 0);

// Finish every entrance at once: for the layout snapshots (tools/ux-snapshot), which
// compare the magazine at rest. Filled in by the layout that is active.
mag.revealAll = () => {};

// ------------------------------------------------------------------------ headings ----
// A heading rises from behind its own baseline: it slides up while a window the size of
// its resting place opens with it.
//
// Done with the Web Animations API on `translate` and `clip-path`, and never by changing
// the heading's DOM. These headings are Blazor markup (often MarkupString), and Blazor
// keeps references to their nodes: splitting the text into lines and putting it back
// (SplitText) left new nodes behind, and the next re-render — switching a Tabs article,
// say — failed on them and took the whole connection down. The animation doesn't persist
// either, so afterwards nothing is left on the element at all; `translate` composes with
// the `transform` the mobile parallax writes instead of fighting it.
mag.initHeadings = (layout, { reduce }) => {
    const headings = [...document.querySelectorAll('.heading-animation')];
    const reveal = (el) => {
        if (el.classList.contains('enable')) {
            return;
        }
        if (reduce) {
            el.classList.add('enable');
            return;
        }
        // skip the stylesheet's top/opacity transition: this rise replaces it
        el.style.transitionDuration = '0s';
        el.classList.add('enable');
        const rise = el.animate([
            { translate: '0 100%', clipPath: 'inset(0% 0% 100% 0%)' },
            { translate: '0 0', clipPath: 'inset(0% 0% 0% 0%)' },
        ], { duration: 1100, easing: 'cubic-bezier(.16, 1, .3, 1)' });
        const done = () => el.style.removeProperty('transition-duration');
        rise.onfinish = done;
        rise.oncancel = done;
    };

    // An IntersectionObserver rather than ScrollTriggers: it follows the element wherever
    // it is shown — the strip, the stacked page, or a lightbox (the calculator popups carry a
    // heading too, hidden until Fancybox moves it on screen, where no trigger measured at
    // load could ever fire).
    const observer = layout.observe(new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                observer.unobserve(entry.target);
                reveal(entry.target);
            }
        });
    }, { threshold: 0.1 }));
    headings.forEach(el => observer.observe(el));

    layout.onEnd(() => headings.forEach(el => el.classList.add('enable')));
    return reveal;
};

// ------------------------------------------------------------------------- desktop ----
mag.initDesktopMotion = (layout, grayboxIds) => {
    const scrollTween = mag.scrollTween;
    if (!scrollTween) {
        return;
    }

    // Parallax: images and panels drift against the strip
    gsap.utils.toArray('.parallax-image').forEach(image => {
        gsap.timeline({
            defaults: { ease: 'none' },
            x: '0vw',
            scrollTrigger: {
                trigger: image,
                containerAnimation: scrollTween,
                start: 'left right',
                end: 'right left',
                scrub: true,
                invalidateOnRefresh: true
            }
        })
            .fromTo(image, { x: 0 }, { x: -700 }, 0);
    });

    grayboxIds.split(',').map(Number).filter(id => id > 0).forEach(id => {
        // Overview articles carry `.gray_box` (which is also a styling class); Quotes uses
        // `.quote-parallax`, a hook with no styling attached to it.
        const selector = `#article-${id} .gray_box, #article-${id} .quote-parallax`;
        gsap.to(selector, {
            x: -300,
            ease: 'none',
            scrollTrigger: {
                trigger: selector,
                containerAnimation: scrollTween,
                scrub: true
            }
        });
    });

    const drift = (selector, x, extra) => {
        if (!document.querySelector(selector)) {
            return;
        }
        gsap.to(selector, {
            x,
            ease: 'none',
            scrollTrigger: {
                trigger: selector,
                containerAnimation: scrollTween,
                scrub: true,
                ...extra
            }
        });
    };
    drift('.perfect__right__gray_box', -400);
    drift('.tot-10-caps-block', 400, { invalidateOnRefresh: true });

    mag.initDesktopEntrances(layout);
};

// Desktop entrances. As an article slides in from the right, its text blocks rise into
// place one after another and its pictures open behind a wipe that runs the same way the
// strip moves. The first article plays it on arrival.
mag.initDesktopEntrances = (layout) => {
    const scrollTween = mag.scrollTween;
    // parallax drives the transform of these; an entrance may fade them, not move them
    const parallaxed = '.parallax-image, .gray_box, .quote-parallax, .perfect__right__gray_box, .tot-10-caps-block';
    const hasPicture = (el) => mag.fxIsMedia(el) ||
        (el.textContent.trim().length === 0 && getComputedStyle(el).backgroundImage !== 'none');

    const pending = new Map();   // element → its entrance
    const late = [];             // triggers made after start-up, outside gsap.matchMedia's reach

    const setUp = (section, index, deferred) => {
        // the article the reader lands on waits a moment for the page to settle
        const arrival = index === 0 ? 0.25 : 0;

        mag.fxBlocks(section).forEach((el, i) => {
            const picture = hasPicture(el);
            const still = el.matches(parallaxed) || !!el.querySelector(parallaxed);
            const from = picture
                ? { clipPath: 'inset(0% 100% 0% 0%)', ...(still ? {} : { scale: 0.96 }) }
                : { opacity: 0, ...(still ? {} : { y: 48 }) };
            const to = picture
                ? { clipPath: 'inset(0% 0% 0% 0%)', ...(still ? {} : { scale: 1 }), duration: 1.3, ease: 'expo.inOut' }
                : { opacity: 1, ...(still ? {} : { y: 0 }), duration: 1, ease: 'power3.out' };
            // back to exactly what the stylesheet says, so nothing differs at rest
            const clear = picture ? (still ? 'clipPath' : 'clipPath,scale') : (still ? 'opacity' : 'opacity,y');

            gsap.set(el, from);
            const entrance = gsap.to(el, {
                ...to,
                delay: arrival + Math.min(i, 4) * 0.12,
                paused: true,
                onComplete: () => {
                    gsap.set(el, { clearProps: clear });
                    pending.delete(el);
                },
            });
            pending.set(el, entrance);

            const trigger = ScrollTrigger.create({
                trigger: el,
                containerAnimation: scrollTween,
                start: 'left 92%',
                once: true,
                onEnter: () => entrance.play(),
            });
            if (deferred) {
                late.push(trigger);
            }
        });
    };

    // The articles on or next to the screen are set up right away; the rest follow in short
    // batches, so starting the magazine doesn't hold the main thread for a few hundred ms
    // (all ~70 triggers at once made one 400ms task on c-hr). They are off screen, and done
    // long before the reader gets there.
    const sections = [...document.querySelectorAll('#wrapper > section')];
    const near = window.innerWidth * 1.5;
    const later = [];
    sections.forEach((section, index) => {
        const box = section.getBoundingClientRect();
        if (box.right > -near && box.left < near) {
            setUp(section, index, false);
        } else {
            later.push([section, index]);
        }
    });
    const batch = () => {
        const until = performance.now() + 12;
        while (later.length && performance.now() < until) {
            setUp(...later.shift(), true);
        }
        if (later.length) {
            layout.timers.push(setTimeout(batch, 0));
        }
    };
    if (later.length) {
        layout.timers.push(setTimeout(batch, 0));
    }

    mag.revealAll = () => {
        // set up whatever is still waiting, so nothing is left to start hidden afterwards
        while (later.length) {
            setUp(...later.shift(), true);
        }
        pending.forEach(entrance => entrance.progress(1));
    };
    layout.onEnd(() => {
        later.length = 0;
        mag.revealAll();
        mag.revealAll = () => {};
        late.forEach(trigger => trigger.kill());
    });
};

// -------------------------------------------------------------------------- mobile ----
mag.initMobileMotion = (layout) => {
    const viewportHeight = () => window.innerHeight || document.documentElement.clientHeight;

    // Follows an element through the viewport: progress 0 as it enters at the bottom → 1
    // as it leaves at the top. `smooth` lets the rendered value trail the scroll a little,
    // which is what makes the motion read as weighted rather than pinned to the scrollbar.
    // `trigger` is measured where it sits, transforms included — so for an element that
    // moves itself, pass something that doesn't.
    const follow = (el, render, smooth, trigger = el) => {
        const state = { p: 0 };
        gsap.to(state, {
            p: 1,
            ease: 'none',
            onUpdate: () => render(state.p),
            scrollTrigger: {
                trigger,
                start: 'top bottom',
                end: 'bottom top',
                scrub: smooth,
                invalidateOnRefresh: true,
            },
        });
    };

    // Parallax image: a slow push-in the whole time it's on screen (1.25 → 1.55), drifting
    // within the headroom the zoom creates (× 0.9, so it never uncovers the frame).
    document.querySelectorAll('.parallax-image').forEach(el => {
        if (el.getClientRects().length === 0) {
            return;
        }
        el.style.willChange = 'transform';
        follow(el, (p) => {
            const scale = 1.25 + p * 0.30;
            const headroom = el.offsetHeight * scale * (scale - 1) / 2;
            const y = (p - 0.5) * 2 * headroom * 0.9;
            el.style.transform = `translate3d(0, ${y.toFixed(2)}px, 0) scale(${scale.toFixed(4)})`;
        }, 0.35, el.parentElement);   // the frame: the image itself zooms and drifts
    });

    // Layered content parallax: the foreground drifts a touch against the image, at a few
    // depths, so a panel reads as stacked planes. 0 when the layer sits centred, so nothing
    // shifts the design; it only moves while you scroll. Depth = travel in px over a pass.
    const drift = (el, depth) => follow(el, (p) => {
        el.style.transform = `translate3d(0, ${(-(p - 0.5) * depth).toFixed(2)}px, 0)`;
    }, 0.35);

    [
        { selector: '.article .heading-animation', depth: 70 },
        { selector: '.article .heading:not(.heading-animation)', depth: 60 },
        { selector: '.article .content', depth: 55 },
        // next to a static icon the text would slide out of line: those rows move as one below
        { selector: '.article .text-block', depth: 55, skipInside: '.text-with-icon' },
        { selector: '.article .gray_box', depth: 85 },
    ].forEach(layer => {
        document.querySelectorAll(layer.selector).forEach(el => {
            // skipped inside a container this layer opts out of, and when not rendered
            // (popup content waiting for its lightbox)
            if ((layer.skipInside && el.closest(layer.skipInside)) || el.getClientRects().length === 0) {
                return;
            }
            // `.heading-animation` reveals with `transition: all`, which would ease the
            // per-frame transform too: limit it to what the reveal animates
            if (el.classList.contains('heading-animation')) {
                el.style.transitionProperty = 'top, opacity';
            }
            drift(el, layer.depth);
        });
    });

    // Icon rows drift as one unit, a little more per row down the list
    document.querySelectorAll('.text-with-icon').forEach(row => {
        if (row.getClientRects().length === 0) {
            return;
        }
        const siblings = row.parentElement ? [...row.parentElement.querySelectorAll(':scope > .text-with-icon')] : [row];
        drift(row, 22 + Math.max(siblings.indexOf(row), 0) * 6);
    });

    mag.initMobileReveals(layout);
    mag.initMobileHandOff(layout, viewportHeight);

    layout.onEnd(() => {
        document.querySelectorAll('.parallax-image, .article .heading-animation, .article .heading, .article .content, .article .text-block, .article .gray_box, .text-with-icon')
            .forEach(el => {
                el.style.transform = '';
                el.style.willChange = '';
                el.style.transitionProperty = '';
            });
    });
};

// Mobile entrances, styled in _effects.scss: blocks rise in, pictures unveil bottom-up.
mag.initMobileReveals = (layout) => {
    document.documentElement.classList.add('fx-on');

    const observed = [];
    const reveal = (el) => {
        el.classList.add('fx-in');
        revealObserver.unobserve(el);
    };
    const revealObserver = layout.observe(new IntersectionObserver((entries) => {
        entries.forEach(entry => entry.isIntersecting && reveal(entry.target));
    }, {
        // a shade inside the viewport edge, so a block has settled by the time it's readable
        rootMargin: '0px 0px -8% 0px',
        threshold: 0
    }));

    document.querySelectorAll('section.article:not(.article-end)').forEach(section => {
        mag.fxBlocks(section).forEach((el, i) => {
            el.classList.add(mag.fxIsMedia(el) ? 'fx-mask' : 'fx-reveal');
            // cap the cascade so the last block isn't kept waiting
            el.style.setProperty('--fx-delay', `${Math.min(i, 4) * 130}ms`);
            observed.push(el);
            revealObserver.observe(el);
        });

        // hero media often sits outside that top-level group
        section.querySelectorAll('.picture, .video-player').forEach(el => {
            if (el.classList.contains('fx-mask') || el.classList.contains('fx-reveal') || el.getClientRects().length === 0) {
                return;
            }
            el.classList.add('fx-mask');
            observed.push(el);
            revealObserver.observe(el);
        });
    });

    // Safety net: a block that is on screen but still hidden (a browser that misreported
    // the intersection) is shown. Only what's on screen — blocks further down still get
    // their entrance when the reader reaches them.
    const net = () => {
        const height = window.innerHeight;
        observed.forEach(el => {
            if (!el.classList.contains('fx-in')) {
                const rect = el.getBoundingClientRect();
                if (rect.top < height && rect.bottom > 0) {
                    reveal(el);
                }
            }
        });
    };
    layout.timers.push(setInterval(net, 1500));

    mag.revealAll = () => observed.forEach(reveal);
    layout.onEnd(() => {
        document.documentElement.classList.remove('fx-on');
        observed.forEach(el => {
            el.classList.remove('fx-reveal', 'fx-mask', 'fx-in');
            el.style.removeProperty('--fx-delay');
        });
        mag.revealAll = () => {};
    });
};

// Hand-off. As an article's last stretch clears the top of the screen it eases back, so
// the next one reads as taking over rather than being stacked underneath:
//  · where the next article paints an opaque background, the outgoing content lags behind
//    the scroll and the next article slides over it (mobile sections are position:
//    relative, so a later one always paints above an earlier one);
//  · where it is transparent there is nothing to hide behind, so the content recedes and
//    dims out instead.
// Applied to `.wrap`, never the `<section>`, which carries the background colour.
mag.initMobileHandOff = (layout, viewportHeight) => {
    const opaque = (el) => {
        if (!el) {
            return false;
        }
        const cs = window.getComputedStyle(el);
        if (cs.backgroundImage && cs.backgroundImage !== 'none') {
            return true;
        }
        const rgba = cs.backgroundColor.match(/[\d.]+/g);
        // no alpha channel listed means fully opaque
        return !!rgba && (rgba.length < 4 || parseFloat(rgba[3]) > 0.95);
    };

    const wraps = [...document.querySelectorAll('section.article:not(.article-end) > .wrap')];
    wraps.forEach(wrap => {
        const section = wrap.parentElement;
        const next = section.nextElementSibling;
        const covered = !!next && next.matches('section') && (opaque(next) || opaque(next.querySelector('.wrap')));

        // 0 while the article still reaches below the halfway line → 1 as its bottom edge
        // leaves the top. Never a longer stretch than the article itself, so a short one
        // isn't dimmed while it's still being read.
        const state = { p: 0 };
        gsap.to(state, {
            p: 1,
            ease: 'none',
            onUpdate: () => {
                const p = state.p;
                if (p === 0) {
                    // untouched: no inline transform, so no stacking context either
                    wrap.classList.remove('fx-exit');
                    wrap.style.opacity = '';
                    wrap.style.transform = '';
                    return;
                }
                wrap.classList.add('fx-exit');
                if (covered) {
                    wrap.style.opacity = (1 - p * 0.35).toFixed(3);
                    wrap.style.transform = `translate3d(0, ${(p * 110).toFixed(2)}px, 0) scale(${(1 - p * 0.04).toFixed(4)})`;
                } else {
                    wrap.style.opacity = (1 - p * 0.85).toFixed(3);
                    wrap.style.transform = `translate3d(0, ${(-p * 44).toFixed(2)}px, 0) scale(${(1 - p * 0.08).toFixed(4)})`;
                }
            },
            scrollTrigger: {
                trigger: section,
                start: () => `bottom ${Math.min(viewportHeight() * 0.5, section.offsetHeight)}px`,
                end: 'bottom top',
                scrub: true,
                invalidateOnRefresh: true,
            },
        });
    });

    layout.onEnd(() => wraps.forEach(wrap => {
        wrap.classList.remove('fx-exit');
        wrap.style.opacity = '';
        wrap.style.transform = '';
    }));
};
